import React, { useMemo, useState } from "react";
import { Plus, Minus } from "lucide-react";

const FAQ_SECTIONS = [
  {
    title: "Ticket purchasing",
    items: [
      {
        question: "How do I buy tickets?",
        answer:
          "Browse events, select your preferred event, choose your seats (if applicable), and proceed to checkout.",
      },
      {
        question: "What payment methods are accepted?",
        answer:
          "You can pay using credit/debit cards, e-wallets (e.g., GCash), or other available payment options.",
      },
      {
        question: "Will I receive a confirmation after purchase?",
        answer:
          "Yes, a confirmation email and your digital ticket (QR code) will be sent after successful payment.",
      },
    ],
  },
  {
    title: "Tickets and entry",
    items: [
      {
        question: "How do I use my ticket?",
        answer: "Simply present your QR code at the event entrance for scanning.",
      },
      {
        question: "Can I use a screenshot of my ticket?",
        answer: "Yes, but make sure the QR code is clear and not altered.",
      },
      {
        question: "What if I don't receive my ticket?",
        answer: "Check your spam folder or contact support if it's not there.",
      },
    ],
  },
  {
    title: "Refunds and cancellations",
    items: [
      {
        question: "Can I cancel or refund my ticket?",
        answer:
          "Tickets are generally non-refundable after purchase. Refunds may be issued for event cancellations, postponements, or significant event changes, subject to the event’s refund terms. Approved refunds will be processed through the original payment method.",
        bullets: [
          "Event cancelled → Eligible for refund",
          "Event postponed → Refund may be requested",
          "Significant event changes → May be eligible",
          "Customer changes their mind/cannot attend → Generally non-refundable",
          "Approved refunds → Processed through the original payment method",
        ],
      },
      {
        question: "What happens if an event is canceled?",
        answer:
          "If an event is officially cancelled, eligible ticket holders may receive a refund according to the event’s refund terms. You will be notified, and approved refunds will be processed through the original payment method.",
      },
    ],
  },
  {
    title: "Technical and account",
    items: [
      {
        question: "Do I need internet access to use my ticket?",
        answer:
          "It's recommended, but you can save your QR code for offline use.",
      },
      {
        question: "Can I transfer my ticket to someone else?",
        answer: "This depends on the event organizer's policy.",
      },
      {
        question: "I entered the wrong email, what should I do?",
        answer: "Contact support immediately so we can resend your ticket.",
      },
    ],
  },
  {
    title: "Seating and events",
    items: [
      {
        question: "Can I choose my seat?",
        answer: "Yes, for events with seat selection enabled.",
      },
      {
        question: "Are seats reserved after adding to cart?",
        answer:
          "Seats are temporarily reserved for a limited time during checkout.",
      },
    ],
  },
];

const Faqs = () => {
  const [openKey, setOpenKey] = useState("0-0");

  const faqSections = useMemo(() => FAQ_SECTIONS, []);

  const toggleItem = (key) => {
    setOpenKey((prev) => (prev === key ? "" : key));
  };

  return (
    <main className="bg-[#0b0b0d] text-white min-h-screen">
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/home_featured_bg.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#0b0b0d]" />

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24 text-center">
          <img src="/assets/echojham.logo2.png" alt="EchoJham" className="h-10 md:h-14 mx-auto mb-4" />
          <h1 className="text-3xl md:text-5xl font-medium tracking-tight">Frequently Asked Questions</h1>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="px-1 md:px-2 py-2 md:py-4">
          <h2 className="text-xl md:text-2xl font-medium tracking-tight mb-5">Frequently Asked Questions</h2>

          {faqSections.map((section, sectionIndex) => (
            <div key={section.title} className="mb-6 last:mb-0">
              <p className="text-[#d3a84e] text-sm md:text-base font-medium tracking-wide mb-3">
                {section.title}
              </p>

              <div>
                {section.items.map((item, itemIndex) => {
                  const itemKey = `${sectionIndex}-${itemIndex}`;
                  const isOpen = openKey === itemKey;

                  return (
                    <div key={item.question} className="border-t border-white/20 last:border-b">
                      <button
                        type="button"
                        onClick={() => toggleItem(itemKey)}
                        className="w-full py-3.5 flex items-center justify-between text-left gap-4"
                        aria-expanded={isOpen}
                      >
                        <span className="text-sm md:text-base font-normal leading-tight text-white/95">{item.question}</span>
                        <span className="shrink-0 text-white/70">
                          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="pb-4 text-xs md:text-sm text-white/75 leading-relaxed max-w-4xl">
                          <p>{item.answer}</p>
                          {Array.isArray(item.bullets) && item.bullets.length > 0 ? (
                            <ul className="mt-3 list-disc space-y-1.5 pl-5">
                              {item.bullets.map((bullet) => (
                                <li key={bullet}>{bullet}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Faqs;
